import { useState } from 'react';
import type { ImageResource } from '@mentimeter/http-clients';
import { CrossIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { ClickableT } from '@mentimeter/ragnar-ui/clickable';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Skeleton } from '@mentimeter/ragnar-ui/skeleton';
import { Image } from '@mentimeter/ragnar-ui/image';
import {
  ModalBody,
  ModalContainer,
  ModalDismiss,
  ModalHeader,
  ModalRoot,
  ModalTrigger,
} from '@mentimeter/ragnar-ui/modal';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { useTranslate } from '../../../features/localization/Translate';

interface QuestionImageT extends ClickableT {
  crop: ImageResource['crop'];
  altText: ImageResource['alt_description'];
  mediumImageUrl: string;
  largeImageUrl: string;
  translateFn: ReturnType<typeof useTranslate>;
}

const DEFAULT_LABEL = 'Question Image';

export const QuestionImage = ({
  largeImageUrl,
  mediumImageUrl,
  translateFn,
  altText,
  crop,
  ...props
}: QuestionImageT) => {
  const [isLoading, setIsLoading] = useState(true);

  // Calculate aspect ratio based on image dimensions
  const aspectRatio = (crop?.height || 1) / (crop?.width || 1);

  return (
    <ModalRoot>
      <ModalTrigger>
        <Clickable
          type="button"
          alignItems="center"
          aria-label={`${translateFn('buttons.show_image_for')} ${altText || DEFAULT_LABEL}`}
          mb="space6"
          width="100%"
          {...props}
        >
          <Box
            alignItems="center"
            borderRadius="lg"
            overflow="hidden"
            maxHeight="300px"
            style={{
              aspectRatio: 1 / aspectRatio,
            }}
          >
            {isLoading && (
              <Skeleton variant="rectangle" width="100%" height="100%" />
            )}
            <Image
              src={mediumImageUrl}
              alt={altText || DEFAULT_LABEL}
              width="100%"
              height="100%"
              style={{
                objectFit: 'contain',
                overflow: 'hidden',
                opacity: isLoading ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            />
          </Box>
        </Clickable>
      </ModalTrigger>
      <ModalContainer size="large" p="space1.5">
        <ScreenReaderOnly>
          <ModalHeader />
        </ScreenReaderOnly>
        <ModalDismiss>
          <Clickable
            type="button"
            aria-label={translateFn('close_image')}
            position="absolute"
            top={0}
            right={0}
            p="space2"
            mr="space4"
            mt="space4"
            bg="bg"
            borderRadius="full"
            zIndex={2}
          >
            <CrossIcon size={3} color="text" />
          </Clickable>
        </ModalDismiss>

        <ModalBody px="space0" pb="space0" pt="space0" borderRadius="xl">
          <Box borderRadius="xl" overflow="hidden">
            <Image src={largeImageUrl} alt={altText ?? 'Image preview'} />
          </Box>
        </ModalBody>
      </ModalContainer>
    </ModalRoot>
  );
};
